import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { SignedIn, SignedOut, UserButton, useUser } from "@clerk/clerk-react";
import { Button } from "./components/Button";
import { useClerkAvailable } from "./clerk-context.js";

function AuthButtons() {
  const navigate = useNavigate();
  const { user } = useUser();

  return (
    <>
      <SignedOut>
        <Button variant="secondary" size="small" onClick={() => navigate("/sign-in")}>
          Sign In
        </Button>
        <Button size="small" onClick={() => navigate("/sign-up")}>
          Create Account
        </Button>
      </SignedOut>
      <SignedIn>
        <span className="hidden text-sm text-kin-muted sm:block">
          {user?.firstName ?? user?.emailAddresses[0]?.emailAddress}
        </span>
        <UserButton
          appearance={{
            elements: {
              avatarBox: "h-8 w-8",
            },
          }}
        />
      </SignedIn>
    </>
  );
}

function MySkillsNav() {
  return (
    <SignedIn>
      <NavLink
        to="/dashboard"
        className={({ isActive }) =>
          `rounded-lg px-3 py-2 text-sm font-medium transition ${
            isActive
              ? "bg-kin-olive/10 text-kin-black"
              : "text-kin-body hover:bg-kin-olive/8 hover:text-kin-black"
          }`
        }
      >
        My Skills
      </NavLink>
    </SignedIn>
  );
}

export function WebsiteShell() {
  const clerkAvailable = useClerkAvailable();

  return (
    <div className="relative min-h-screen overflow-x-clip bg-kin-sunrise">
      {/* Ambient blur blobs */}
      <div className="pointer-events-none fixed -left-28 -top-24 -z-20 h-[34rem] w-[34rem] rounded-full bg-kin-lavender/20 blur-3xl" />
      <div className="pointer-events-none fixed right-[-8rem] top-28 -z-20 h-[30rem] w-[30rem] rounded-full bg-kin-gold/15 blur-3xl" />
      <div className="pointer-events-none fixed bottom-[-9rem] left-1/3 -z-20 h-[26rem] w-[26rem] rounded-full bg-kin-orange/15 blur-3xl" />

      {/* Floating glass header */}
      <header className="sticky top-0 z-50 px-6 pt-4 sm:px-8">
        <div className="mx-auto flex max-w-6xl items-center justify-between rounded-2xl border border-kin-olive/12 bg-kin-white/78 px-4 py-3 backdrop-blur-md sm:px-5">
          <NavLink to="/" className="flex flex-col leading-none">
            <span className="font-serif text-2xl font-bold text-kin-black">Kin</span>
            <span className="mt-0.5 text-[10px] font-semibold tracking-[0.14em] uppercase text-kin-muted">
              Your Health, Intelligent
            </span>
          </NavLink>

          <nav className="hidden items-center gap-1 sm:flex">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `rounded-lg px-3 py-2 text-sm font-medium transition ${
                  isActive
                    ? "bg-kin-olive/10 text-kin-black"
                    : "text-kin-body hover:bg-kin-olive/8 hover:text-kin-black"
                }`
              }
            >
              Vault
            </NavLink>
            <NavLink
              to="/marketplace"
              className={({ isActive }) =>
                `rounded-lg px-3 py-2 text-sm font-medium transition ${
                  isActive
                    ? "bg-kin-olive/10 text-kin-black"
                    : "text-kin-body hover:bg-kin-olive/8 hover:text-kin-black"
                }`
              }
            >
              Marketplace
            </NavLink>
            <NavLink
              to="/company"
              className={({ isActive }) =>
                `rounded-lg px-3 py-2 text-sm font-medium transition ${
                  isActive
                    ? "bg-kin-olive/10 text-kin-black"
                    : "text-kin-body hover:bg-kin-olive/8 hover:text-kin-black"
                }`
              }
            >
              Company
            </NavLink>
            {clerkAvailable && <MySkillsNav />}
          </nav>

          <div className="flex items-center gap-2">{clerkAvailable && <AuthButtons />}</div>
        </div>
      </header>

      {/* Page content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-kin-olive/10 bg-kin-white/40 px-6 py-10 sm:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="font-serif text-xl font-bold text-kin-black">Kin</p>
              <p className="mt-0.5 text-xs text-kin-muted">Your Health, Intelligent</p>
            </div>
            <nav className="flex flex-wrap gap-x-6 gap-y-2">
              {[
                ["Vault", "/"],
                ["Marketplace", "/marketplace"],
                ["Company", "/company"],
                ["Privacy Policy", "/privacy"],
                ["Terms of Service", "/terms"],
              ].map(([label, href]) => (
                <NavLink
                  key={label}
                  to={href}
                  className="text-sm text-kin-muted transition hover:text-kin-body"
                >
                  {label}
                </NavLink>
              ))}
            </nav>
          </div>
          <p className="mt-8 text-xs text-kin-muted">
            © 2026 Kin Health. All rights reserved. Kin supports clinician workflows but does not
            replace diagnosis or emergency care.
          </p>
        </div>
      </footer>
    </div>
  );
}
